import { PERMISSIONS as USER_PROFILE_PERMISSIONS } from '@/pages/user/user-profile/permissions';
import { authService } from '@/services/auth/authService';
import routes from '../config/routes';

/**
 * @see https://umijs.org/docs/max/access#access
 * Following Ant Design Pro authority management patterns
 *
 * This function runs on every route change and determines access permissions.
 * It uses initialState as primary source, with localStorage as fallback.
 * */
export default function access(
  initialState: { currentUser?: API.CurrentUser } | undefined,
) {
  const { currentUser } = initialState ?? {};

  // Get user from localStorage as fallback (for robustness during initialization)
  const cachedUser = authService.getCachedUser();

  // Use initialState if available, otherwise fall back to localStorage
  // This ensures access checks work even during app initialization
  const isAuthenticated = !!currentUser || !!cachedUser;

  // Helper function to check page access using raw backend pages
  const checkPageAccess = (pageId: string) => {
    // First check if user is authenticated at all
    if (!isAuthenticated) return false;

    // Get user data from localStorage (source of truth for permissions)
    const user = authService.getCachedUser();
    if (!user || !user.pages) return false;

    const hasPage = user.pages.some((page: any) => page.identifier === pageId);

    // if (process.env.NODE_ENV === 'development') {
    //   console.log('🔒 Access check for page:', pageId, '→', hasPage, {
    //     hasInitialState: !!currentUser,
    //     hasCachedUser: !!cachedUser,
    //     pagesCount: user.pages?.length || 0,
    //   });
    // }

    return hasPage;
  };

  // Helper function to check action access using raw backend pages
  const checkActionAccess = (pageId: string, actionId: string) => {
    if (!isAuthenticated) return false;

    const user = authService.getCachedUser();
    if (!user || !user.pages) return false;

    const page = user.pages.find((p: any) => p.identifier === pageId);
    if (!page) return false;

    return (
      page.actions?.some((action: any) => action.identifier === actionId) ||
      false
    );
  };

  // Named access keys for routes gated by an action instead of a page
  const customAccessChecks: Record<string, () => boolean> = {
    canViewWatchlist: () =>
      checkActionAccess(
        USER_PROFILE_PERMISSIONS.PAGE,
        USER_PROFILE_PERMISSIONS.ACTIONS.VIEW_USER_WATCHLIST,
      ),
  };

  /**
   * Configure parent menu visibility based on children access
   * Parent routes are automatically shown/hidden based on their children's access
   * Returns false when no children are accessible (hides parent menu)
   */
  const configureParentMenuVisibility = (
    accessFunctions: Record<string, () => boolean>,
  ) => {
    const parentChildMap: Record<string, string[]> = {};

    const scanRoutes = (routeList: any[]) => {
      for (const route of routeList) {
        // Process all parent routes that have children
        if (route.routes?.length) {
          const childAccesses = route.routes
            .filter((c: any) => c.access && !c.redirect)
            .map((c: any) => c.access);

          // Process if there are children with access
          if (childAccesses.length > 0) {
            // Use route.name as the access key (parent routes should have access: route.name)
            const parentAccessKey = route.name || route.access;
            if (parentAccessKey) {
              parentChildMap[parentAccessKey] = childAccesses;
            }
          } else {
            // Even if no children have access, create access function that returns false
            // This ensures parent is hidden when no children are accessible
            const parentAccessKey = route.name || route.access;
            if (parentAccessKey) {
              parentChildMap[parentAccessKey] = [];
            }
          }
        }

        // Recurse into nested routes
        if (route.routes) scanRoutes(route.routes);
        if (route.children) scanRoutes(route.children);
      }
    };

    scanRoutes(routes);

    // Create access functions: only show parent if at least one child is accessible
    for (const [parentAccess, childAccesses] of Object.entries(
      parentChildMap,
    )) {
      accessFunctions[parentAccess] = () => {
        // If no children have access codes, return false (hide parent)
        if (childAccesses.length === 0) {
          return false;
        }
        // Return true only if at least one child is accessible
        return childAccesses.some(
          (child) => customAccessChecks[child]?.() ?? checkPageAccess(child),
        );
      };
    }
  };

  // Create dynamic access functions for all user's pages
  const createDynamicAccessFunctions = () => {
    const user = authService.getCachedUser();
    if (!user || !user.pages) return {};

    const accessFunctions: Record<string, () => boolean> = {};

    // Create access function for each page the user has
    user.pages.forEach((page: any) => {
      accessFunctions[page.identifier] = () => checkPageAccess(page.identifier);
    });

    // Configure parent menu visibility based on children access
    configureParentMenuVisibility(accessFunctions);

    return accessFunctions;
  };

  return {
    // Admin access (check both sources)
    canAdmin:
      (currentUser && currentUser.access === 'admin') ||
      cachedUser?.roles?.some((role) => role.name === 'admin'),

    // Page-level access check
    canAccessPage: (pageId: string) => checkPageAccess(pageId),

    // Route-level access check (by page identifier)
    canAccessRoute: (pageId: string) => checkPageAccess(pageId),

    // Action-level access check using raw backend pages
    canPerformAction: (pageId: string, actionId: string) =>
      checkActionAccess(pageId, actionId),

    // UI locales permitted by Permissions API (`allowedCulture`)
    canAccessCulture: (locale: string) => authService.canAccessCulture(locale),
    getAllowedCultures: () => authService.getAllowedCultures(),

    // Named action-gated route access keys
    ...customAccessChecks,

    // Dynamic page access functions - automatically created from user's permissions
    ...createDynamicAccessFunctions(),
  };
}

/** Use from any page/module to read allowed UI cultures (persisted with permissions). */
export function getAllowedCultures(): string[] {
  return authService.getAllowedCultures();
}

/** Use from any page/module to check if a locale is allowed for the current user. */
export function canAccessCulture(locale: string): boolean {
  return authService.canAccessCulture(locale);
}
